import { useParams, useNavigate } from "react-router-dom";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { useOrderLegSummaries } from "@/hooks/useOrderLegSummaries";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { format, isPast, parseISO } from "date-fns";
import { formatTimeWithTz, formatWindow } from "@/lib/order-time";
import type { Location } from "@/types/tms";
import {
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Clock,
  Calendar,
  Truck,
  Building2,
  Pencil,
  Trash2,
  Repeat,
  History,
  User,
  MoreHorizontal,
} from "lucide-react";
import { estimateTravelMinutes, addMinutesToDatetime } from "@/lib/travel-time";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { OrderFormDialog } from "@/components/orders/OrderFormDialog";
import { OrderFormData, initialFormData } from "@/components/orders/orderFormTypes";
import { useOrderCancellation } from "@/hooks/useOrderDeletion";
import {
  templateToOrderFormData,
  orderFormToTemplatePatch,
  orderFormToTemplateLegRows,
  isOpenEndedTemplate,
  isLegacyTemplate,
  templateTimezone,
  type TemplateLegRow,
  type TemplateRow,
} from "@/lib/recurringTemplateForm";
import { PLANNING_TZ } from "@/lib/timezone";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import type { Customer, Tractor, Driver } from "@/types/tms";
import { formatInTimeZone } from "date-fns-tz";
import { DAYS_OF_WEEK, ORDER_STATUS_LABELS, OrderStatus } from "@/types/tms";
import { useState, useEffect, useRef } from "react";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapContainer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ThemedTileLayer } from "@/components/atlas/ThemedTileLayer";

const DAY_SHORT: Record<number, string> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

// Map icons (same as OrderDetail)
const originIcon = L.divIcon({
  className: "",
  html: `<div style="background:#22c55e;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)">A</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const destIcon = L.divIcon({
  className: "",
  html: `<div style="background:#ef4444;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)">B</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function FitBounds({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (coords.length >= 2 && !fitted.current) {
      fitted.current = true;
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => [lat, lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [coords, map]);
  return null;
}

async function fetchRoute(
  origin: [number, number],
  dest: [number, number],
): Promise<[number, number][] | null> {
  try {
    const { data } = await supabase.functions.invoke("mapbox-directions", {
      body: {
        origin: [origin[1], origin[0]],
        destination: [dest[1], dest[0]],
      },
    });
    return data?.coordinates ?? null;
  } catch {
    return null;
  }
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

export default function RecurringTemplateDetail() {
  const viewerTz = useViewerTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: trailerTypes } = useTrailerTypes();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const { bulkCancelAsync } = useOrderCancellation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<OrderFormData>(initialFormData);

  // Map state
  const [originCoord, setOriginCoord] = useState<[number, number] | null>(null);
  const [destCoord, setDestCoord] = useState<[number, number] | null>(null);
  const [routeCoords, setRouteCoords] = useState<[number, number][] | null>(null);

  // Fetch template with billing party
  const { data: template, isLoading } = useQuery({
    queryKey: ["recurring-template-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recurring_order_templates")
        .select(`
          *,
          billing_party:customers!recurring_order_templates_billing_party_id_fkey(id, organization_name, short_code),
          ship_from:locations!recurring_order_templates_ship_from_location_id_fkey(short_code, street_address, city, province_state, postal_zip_code, latitude, longitude, origin_latitude, origin_longitude, destination_latitude, destination_longitude),
          ship_to:locations!recurring_order_templates_ship_to_location_id_fkey(short_code, street_address, city, province_state, postal_zip_code, latitude, longitude, origin_latitude, origin_longitude, destination_latitude, destination_longitude),
          recurring_order_template_legs(*)
        `)
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch order instances
  const { data: instances } = useQuery({
    queryKey: ["template-instances", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id, unique_id, status, ready_time, ready_time_end, requested_delivery_time, requested_delivery_time_end,
          ship_from_street_address, ship_from_city, ship_from_province_state,
          ship_to_street_address, ship_to_city, ship_to_province_state,
          assigned_tractor:tractors(name),
          assigned_operator:drivers!assigned_operator_id(name),
          assigned_operator_2:drivers!assigned_operator_2_id(name)
        `)
        .eq("recurring_order_template_id", id!)
        .order("ready_time", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // One batched leg-status lookup for the generated orders, so multi-leg
  // instances show the leg-derived rollup chip.
  const { legsFor: instanceLegsFor } = useOrderLegSummaries(
    (instances ?? []).map((o: { id: string }) => o.id),
  );


  // Locations (used to resolve each instance's stop timezone for display)
  const { data: locations } = useQuery({
    queryKey: ["locations-for-tz"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("id, short_code, street_address, city, province_state, latitude, longitude, origin_latitude, origin_longitude, destination_latitude, destination_longitude")
        .eq("archived", false);
      if (error) throw error;
      return data as Location[];
    },
  });

  // Delete template mutation — routes booked orders through the canonical
  // `bulkCancelAsync` (SOX-compliant, cancels upstream EAPI first) so this
  // path stays in lock-step with the Customers > Recurring templates sub-table.
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { data: bookedRows, error: fetchErr } = await supabase
        .from("orders")
        .select("id")
        .eq("recurring_order_template_id", id!)
        .eq("status", "booked");
      if (fetchErr) throw fetchErr;
      if (bookedRows && bookedRows.length > 0) {
        await bulkCancelAsync(
          bookedRows.map((r) => r.id),
          { reason: "Template deleted", notes: "Recurring order template was deleted" },
        );
      }

      const { error } = await supabase
        .from("recurring_order_templates")
        .delete()
        .eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Template deleted and associated booked orders canceled");
      navigate("/customers");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Lookups required by OrderFormDialog templateEditMode.
  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data } = await supabase.from("customers").select("*").order("organization_name");
      return (data || []) as Customer[];
    },
  });
  const { data: tractors } = useQuery({
    queryKey: ["tractors"],
    queryFn: async () => {
      const { data } = await supabase.from("tractors").select("*").order("name");
      return (data || []) as Tractor[];
    },
  });
  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data } = await supabase.from("drivers").select("*").order("name");
      return (data || []) as Driver[];
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async (fd: OrderFormData) => {
      if (!id || !tenantId) throw new Error("Missing template id or tenant");
      const patch = orderFormToTemplatePatch(fd);
      const isMultiLeg = fd.order_type === "multi-leg";

      const { error: tplErr } = await supabase
        .from("recurring_order_templates")
        .update(patch as any)
        .eq("id", id);
      if (tplErr) throw tplErr;

      if (isMultiLeg) {
        const legRows = orderFormToTemplateLegRows(fd, id, tenantId, locations);
        const { error: delErr } = await supabase
          .from("recurring_order_template_legs")
          .delete()
          .eq("template_id", id);
        if (delErr) throw delErr;
        if (legRows.length > 0) {
          const { error: insErr } = await supabase
            .from("recurring_order_template_legs")
            .insert(legRows as any);
          if (insErr) throw insErr;
        }
      } else {
        // Single-leg edits should not leave stale leg rows behind if the
        // template was previously multi-leg. Delete the blueprint so the
        // generator falls back to header-only occurrence creation.
        const { error: delErr } = await supabase
          .from("recurring_order_template_legs")
          .delete()
          .eq("template_id", id);
        if (delErr) throw delErr;
      }

      // Best-effort inline regeneration for the 48h window.
      supabase.functions
        .invoke("trigger-template-generation", { body: { template_id: id } })
        .catch((err) => console.warn("Inline template regeneration failed:", err));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-template-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["template-instances", id] });
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Template updated.");
      setIsEditOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleEditClick = () => {
    if (!template) return;
    const legRows = ((template as any).recurring_order_template_legs || []) as TemplateLegRow[];
    setEditFormData(
      templateToOrderFormData(template as unknown as TemplateRow, legRows, locations),
    );
    setIsEditOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateTemplateMutation.mutate(editFormData);
  };

  const handleEditReset = () => {
    setIsEditOpen(false);
    setEditFormData(initialFormData);
  };

  // Geocode locations for map
  const shipFrom = template?.ship_from as any;
  const shipTo = template?.ship_to as any;

  useEffect(() => {
    if (!shipFrom || !shipTo) return;
    const fromLat = shipFrom?.origin_latitude;
    const fromLng = shipFrom?.origin_longitude;
    const toLat = shipTo?.destination_latitude;
    const toLng = shipTo?.destination_longitude;

    if (fromLat != null && fromLng != null && toLat != null && toLng != null) {
      const from: [number, number] = [fromLat, fromLng];
      const to: [number, number] = [toLat, toLng];
      setOriginCoord(from);
      setDestCoord(to);
      fetchRoute(from, to).then(setRouteCoords);
    }
  }, [shipFrom?.origin_latitude, shipFrom?.origin_longitude, shipTo?.destination_latitude, shipTo?.destination_longitude]);

  if (isLoading) {
    return (
      <AppLayout title="">
        <div className="space-y-6 animate-fade-in">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-[300px] lg:col-span-2" />
            <Skeleton className="h-[300px]" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!template) {
    return (
      <AppLayout title="Template Not Found">
        <div className="text-center py-16 space-y-4">
          <p className="text-muted-foreground">This recurring order template could not be found.</p>
          <Button variant="outline" onClick={() => navigate("/customers")}>
            Back to Customers
          </Button>
        </div>
      </AppLayout>
    );
  }

  const isExpired =
    !!template.end_date &&
    !isOpenEndedTemplate(template.end_date) &&
    isPast(parseISO(template.end_date));
  const billingParty = template.billing_party as any;

  // "First occurrence hasn't been generated yet" empty-state trigger.
  // The generator only materializes the next few days, counted in the
  // template's own timezone; when start_date is further out, an empty
  // instances table is expected — say so explicitly instead of leaving users
  // to guess whether creation failed.
  const templateTz = templateTimezone((template as any).timezone);
  const legacyTz = isLegacyTemplate(template as any);
  const tomorrowInTemplateTz = formatInTimeZone(
    new Date(Date.now() + 24 * 60 * 60 * 1000),
    templateTz,
    "yyyy-MM-dd",
  );
  const startsInFuture =
    !!template.start_date &&
    template.start_date > tomorrowInTemplateTz;
  const tzWord = templateTz === PLANNING_TZ ? "Central" : "Pacific";

  const noInstancesYet = (instances?.length ?? 0) === 0 && startsInFuture;

  // Status summary
  const statusCounts = (instances || []).reduce<Record<string, number>>((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const filteredInstances = statusFilter === "all"
    ? instances || []
    : (instances || []).filter((o) => o.status === statusFilter);

  const recurrenceDayLabels = (template.recurrence_days || [])
    .sort((a: number, b: number) => a - b)
    .map((d: number) => DAY_SHORT[d] || d)
    .join(", ");

  // Compute delivery time from coordinates or fallback to trip_duration_minutes
  const computeDeliveryTime = () => {
    const depTime = template.departure_time; // e.g. "08:00:00"
    if (!depTime) return null;
    const [hStr, mStr] = depTime.split(":");
    const depHour = parseInt(hStr, 10);
    const depMin = parseInt(mStr, 10);
    if (isNaN(depHour) || isNaN(depMin)) return null;

    let travelMins: number;
    if (shipFrom?.origin_latitude != null && shipFrom?.origin_longitude != null && shipTo?.destination_latitude != null && shipTo?.destination_longitude != null) {
      travelMins = estimateTravelMinutes(shipFrom.origin_latitude, shipFrom.origin_longitude, shipTo.destination_latitude, shipTo.destination_longitude);
    } else {
      travelMins = template.trip_duration_minutes;
    }

    const ref = `2025-01-01T${String(depHour).padStart(2, "0")}:${String(depMin).padStart(2, "0")}`;
    const result = addMinutesToDatetime(ref, travelMins);
    const timePart = result.split("T")[1];
    return timePart || null;
  };

  // Template times are recurring wall-clock times in the template's zone, so
  // they're labeled with the zone name rather than a dated CST/CDT.
  const readyTimeFormatted = template.departure_time
    ? `${template.departure_time.substring(0, 5)} ${tzWord}`
    : "—";
  const deliveryTimeFormatted = computeDeliveryTime() ? `${computeDeliveryTime()} ${tzWord}` : "—";

  // Map data
  const hasCoords = originCoord && destCoord;
  const mapCenter: [number, number] = hasCoords
    ? [(originCoord[0] + destCoord[0]) / 2, (originCoord[1] + destCoord[1]) / 2]
    : [43.65, -79.38];

  const breadcrumbTitle = (
    <Breadcrumb>
      <BreadcrumbList className="text-xl font-semibold">
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/customers" className="text-xl font-semibold">Customers</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link
              to={billingParty?.id ? `/customers/${billingParty.id}` : "/customers"}
              className="text-xl font-semibold"
            >
              {billingParty?.organization_name || "Customer"}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage className="text-xl font-semibold">{template.unique_id}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <AppLayout
      title=""
      titleExtra={breadcrumbTitle}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEditClick}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      {/* Delete confirmation handled inline */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border rounded-lg p-6 max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold">Delete template?</h3>
            <p className="text-sm text-muted-foreground">
              This will permanently delete the template and all associated booked/canceled orders. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit dialog — templateEditMode reuses the exact create-form UI so
          single- and multi-leg templates round-trip through the same fields. */}
      <OrderFormDialog
        isOpen={isEditOpen}
        onOpenChange={(open) => (open ? setIsEditOpen(true) : handleEditReset())}
        editingOrder={null}
        formData={editFormData}
        setFormData={setEditFormData}
        onSubmit={handleEditSubmit}
        onReset={handleEditReset}
        isSubmitting={updateTemplateMutation.isPending}
        customers={customers}
        tractors={tractors}
        drivers={drivers}
        locations={locations}
        templateEditMode
        lockBillingPartyId={billingParty?.id}
      />

      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Recurring order summary</CardTitle>
                  <div className="flex items-center gap-2">
                    {legacyTz && (
                      <Badge
                        variant="outline"
                        data-testid="legacy-tz-badge"
                        title="Created before planning moved to Central. Edit and save to convert — real pickup times stay the same."
                      >
                        Pacific (legacy) — edit &amp; save to convert
                      </Badge>
                    )}
                    <Badge variant={isExpired ? "secondary" : "default"} className="flex items-center gap-1.5">
                      <Repeat className="h-3 w-3" />
                      {isExpired ? "Expired" : "Active"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Map */}
                <div className="rounded-lg overflow-hidden border h-[320px]">
                  <MapContainer
                    center={mapCenter}
                    zoom={6}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                    key={id}
                  >
                    <ThemedTileLayer />
                    {originCoord && <Marker position={originCoord} icon={originIcon} />}
                    {destCoord && <Marker position={destCoord} icon={destIcon} />}
                    {hasCoords && (
                      <>
                        {routeCoords ? (
                          <Polyline
                            positions={routeCoords}
                            pathOptions={{ color: "hsl(var(--primary))", weight: 4 }}
                          />
                        ) : (
                          <Polyline
                            positions={[originCoord, destCoord]}
                            pathOptions={{ color: "hsl(var(--primary))", weight: 3, dashArray: "8 4" }}
                          />
                        )}
                        <FitBounds coords={[originCoord, destCoord]} />
                      </>
                    )}
                  </MapContainer>
                </div>

                {/* Route */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                      <p className="text-lg font-bold">{shipFrom?.short_code || "—"}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[shipFrom?.street_address, shipFrom?.city, shipFrom?.province_state, shipFrom?.postal_zip_code].filter(Boolean).join(", ")}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <ArrowDownRight className="h-4 w-4 text-muted-foreground" />
                      <p className="text-lg font-bold">{shipTo?.short_code || "—"}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[shipTo?.street_address, shipTo?.city, shipTo?.province_state, shipTo?.postal_zip_code].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </div>

                {/* Times — styled like order detail */}
                <div className="border-t pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-base font-semibold text-foreground">
                      Ready Time{" "}
                      <span className="text-muted-foreground">at {readyTimeFormatted}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-foreground">
                      Delivery Time{" "}
                      <span className="text-muted-foreground">at {deliveryTimeFormatted}</span>
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  {/* Schedule */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Schedule</h4>
                    <DetailRow label="Days" value={recurrenceDayLabels || "—"} />
                    <DetailRow label="Start Date" value={format(parseISO(template.start_date), "MMM d, yyyy")} />
                    <DetailRow
                      label="End Date"
                      value={
                        isOpenEndedTemplate(template.end_date)
                          ? "No end date"
                          : format(parseISO(template.end_date), "MMM d, yyyy")
                      }
                    />
                  </div>

                  {/* Equipment & Billing */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Details</h4>
                    <DetailRow
                      label="Trailer Type"
                      value={getTrailerTypeLabel(template.trailer_type_id, trailerTypes)}
                    />
                    <DetailRow
                      label="Billing Party"
                      value={billingParty?.organization_name || "—"}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Instances Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Order Instances</CardTitle>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {Object.entries(ORDER_STATUS_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Summary row */}
                {instances && instances.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {instances.length} order{instances.length !== 1 ? "s" : ""}
                    {Object.entries(statusCounts).map(([status, count]) => (
                      <span key={status}> · {count} {ORDER_STATUS_LABELS[status as OrderStatus] || status}</span>
                    ))}
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ready Time</TableHead>
                      <TableHead>Delivery Time</TableHead>
                      <TableHead>Tractor</TableHead>
                      <TableHead>Operator</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInstances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No orders found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInstances.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>
                            <Link
                              to={`/orders/${order.id}`}
                              className="font-mono text-sm text-primary hover:underline"
                            >
                              {order.unique_id}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <OrderStatusBadge status={order.status} legs={instanceLegsFor(order.id)} />
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatTimeWithTz(order.ready_time, viewerTz, "MMM d, yyyy")}
                            <span className="text-muted-foreground"> at {formatWindow((order as any).ready_time, (order as any).ready_time_end, viewerTz, "HH:mm")}</span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatTimeWithTz(order.requested_delivery_time, viewerTz, "MMM d, yyyy")}
                            <span className="text-muted-foreground"> at {formatWindow((order as any).requested_delivery_time, (order as any).requested_delivery_time_end, viewerTz, "HH:mm")}</span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {(order.assigned_tractor as any)?.name || "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {(order.assigned_operator as any)?.name || "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {noInstancesYet && (
                  <div className="border-t px-4 py-3 text-xs text-muted-foreground">
                    First occurrence generates on{" "}
                    <span className="font-medium">
                      {format(parseISO(template.start_date), "MMM d, yyyy")}
                    </span>
                    . The nightly job materializes orders 24–48h in advance.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">
            {/* Template History */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <History className="h-4 w-4" /> Template History
              </h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Created by:</span>
                  <span className="font-medium">{template.created_by || "Unknown"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Created at:</span>
                  <span className="font-medium">
                    {format(new Date(template.created_at), "MMM d, yyyy h:mm a")}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Total orders:</span>
                  <span className="font-medium">{instances?.length ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
